// `abstract new` rather than `new`: an abstract constructor type accepts BOTH
// abstract and concrete classes (a concrete constructor always widens to an
// abstract-typed one), so autoAttrs composes with mixins like
// HistoryTrackingMixin that hand back an abstract class expecting the final
// leaf class to implement their abstract members.
type Constructor<T = HTMLElement> = abstract new (...args: any[]) => T;

/**
 * A spec is just the kebab-case attribute name (string shorthand, the common
 * case — a reflected string property) or, for a boolean/presence attribute,
 * `{ attr, kind: 'boolean' }`. The property name is never written out — it's
 * derived from `attr` by stripping dashes and camelCasing.
 */
export type AttrSpec = string | { readonly attr: string; readonly kind: 'boolean' };

type SpecAttr<S extends AttrSpec> = S extends string ? S : S extends { attr: infer A extends string } ? A : never;
type SpecKind<S extends AttrSpec> = S extends string ? 'string' : S extends { kind: infer K extends 'boolean' } ? K : 'string';

// Type-level mirror of the runtime toCamelCase() below: kebab-case -> camelCase.
type CamelCase<S extends string> = S extends `${infer Head}-${infer Rest}`
  ? `${Head}${Capitalize<CamelCase<Rest>>}`
  : S;

function toCamelCase(attr: string): string {
  return attr.replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());
}

type AutoAttrsShape<Specs extends readonly AttrSpec[]> = {
  [S in Specs[number] as CamelCase<SpecAttr<S>>]: SpecKind<S> extends 'boolean' ? boolean : string | null;
};

/**
 * Mixin: declares get/set property accessors — one per spec, defined once on
 * the prototype — that mirror an HTML attribute 1:1. The property name is
 * derived from the attribute name (kebab-case -> camelCase), so a spec never
 * repeats itself.
 *
 * Usage:
 *   class MyEl extends autoAttrs(HTMLElement, [
 *     'label-text',
 *     { attr: 'hidden-404', kind: 'boolean' },
 *   ] as const) { ... }
 *
 *   el.labelText  // string | null, mirrors getAttribute('label-text')
 *   el.hidden404  // boolean, mirrors hasAttribute('hidden-404')
 *
 * Does not touch observedAttributes/attributeChangedCallback — the consuming
 * class still declares those itself so it controls exactly when to re-render
 * (map the same spec list with attrNameOf() to build it without repeating names).
 *
 * Composes with other mixins in either position, e.g.
 *   class AsFileInput extends autoAttrs(HistoryTrackingMixin(HTMLElement), SPECS) { ... }
 * It adds no lifecycle methods (no connectedCallback/attributeChangedCallback
 * of its own) — just passive accessors on the prototype — so it never needs
 * to call super and never shadows another mixin's hooks.
 */
export function autoAttrs<
  TBase extends Constructor<HTMLElement>,
  const Specs extends readonly AttrSpec[]
>(Base: TBase, specs: Specs): TBase & Constructor<InstanceType<TBase> & AutoAttrsShape<Specs>> {
  abstract class AutoAttrsHost extends Base {}
  for (const spec of specs) {
    const attrName = typeof spec === 'string' ? spec : spec.attr;
    const kind = typeof spec === 'string' ? 'string' : spec.kind;
    const prop = toCamelCase(attrName);
    Object.defineProperty(AutoAttrsHost.prototype, prop, kind === 'boolean'
      ? {
          get(this: HTMLElement): boolean { return this.hasAttribute(attrName); },
          set(this: HTMLElement, value: boolean) {
            if (value) this.setAttribute(attrName, '');
            else this.removeAttribute(attrName);
          },
          enumerable: true,
          configurable: true,
        }
      : {
          get(this: HTMLElement): string | null { return this.getAttribute(attrName); },
          set(this: HTMLElement, value: string | null) {
            if (value === null) this.removeAttribute(attrName);
            else this.setAttribute(attrName, value);
          },
          enumerable: true,
          configurable: true,
        });
  }
  return AutoAttrsHost as any;
}

/** Pulls the attribute name out of a spec — use when building
 *  observedAttributes from the same spec list passed to autoAttrs(). */
export function attrNameOf(spec: AttrSpec): string {
  return typeof spec === 'string' ? spec : spec.attr;
}

type PrefixOne<S extends AttrSpec, P extends string> =
  S extends string ? `${P}-${S}` : { attr: `${P}-${SpecAttr<S>}`; kind: 'boolean' };

type PrefixSpec<S extends AttrSpec, P extends string, IgnoreUnion extends string> =
  SpecAttr<S> extends IgnoreUnion ? S : PrefixOne<S, P>;

// Mapped *tuple* type (not `Specs[number]`) so element order/arity survive —
// each position keeps its own prefixed-or-not spec.
type PrefixedAttrs<Specs extends readonly AttrSpec[], P extends string, IgnoreUnion extends string> = {
  [I in keyof Specs]: Specs[I] extends AttrSpec ? PrefixSpec<Specs[I], P, IgnoreUnion> : Specs[I];
};

/**
 * Returns a copy of `specs` with `${prefix}-` prepended to every attribute
 * name, except those listed in `ignore` (kept bare). For merging one
 * component's attribute list into another's without the merged names
 * colliding or reading as ambiguous — e.g. as-import-group merges
 * as-file-input's passthrough attrs under a `file-` prefix so
 * `group.fileMultiple` reads as clearly distinct from `group.fileInput.multiple`.
 *
 * Used by as-import-group.ts's PREFIXED_FILE_INPUT_ATTRS: attributes already
 * shown in shipped markup or the published docs (category, accept,
 * intercepts-dropped, button-text, drop-text, minimal-file-list, trcad-param,
 * configure-on-change, no-accepts-label) are passed via `ignore` and stay
 * bare — introducing the prefix on an already-shipped attribute would be a
 * breaking rename for existing markup.
 */
export function prefixAttrs<
  const Specs extends readonly AttrSpec[],
  P extends string,
  const Ignore extends readonly string[] = []
>(specs: Specs, prefix: P, opts?: { ignore?: Ignore }): PrefixedAttrs<Specs, P, Ignore[number]> {
  const ignore = new Set<string>(opts?.ignore ?? []);
  return specs.map((spec) => {
    const name = attrNameOf(spec);
    if (ignore.has(name)) return spec;
    const prefixed = `${prefix}-${name}`;
    return typeof spec === 'string' ? prefixed : { attr: prefixed, kind: spec.kind };
  }) as any;
}
